import { Routes, Router, CanActivateFn } from '@angular/router';
import { HomeComponent } from './features/home/home.component';
import { ReportsComponent } from './features/reports/reports.component';
import { ProfileComponent } from './features/profile/profile.component';
import { TripListComponent } from './features/trips/trip-list.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { inject } from '@angular/core';
import { AuthService } from './core/services/auth.service';
import {
    UiVersionService,
    equivalentUrl,
    hasSharedEquivalent,
    shellSubPath,
} from './core/services/ui-version.service';
import { map } from 'rxjs/operators';

const authGuard = () => {
    const auth = inject(AuthService);
    const router = inject(Router);
    return auth.user$.pipe(
        map(user => {
            if (user) return true;
            return router.createUrlTree(['/login']);
        })
    );
};

const adminGuard = () => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const adminEmails = ['josephvijayakumar.j@gmail.com', 'kanithan.official@gmail.com'];
    return auth.user$.pipe(
        map(user => {
            if (user && adminEmails.includes(user.email || '')) return true;
            return router.createUrlTree(['/']);
        })
    );
};

/**
 * Keeps hardcoded /v2 links (inside screens reused by the v3 shell) in the v3 shell when the
 * device prefers v3. Switching to v2 sets the preference first, so this never loops.
 */
const v2VersionGuard: CanActivateFn = (_route, state) => {
    const uiVersion = inject(UiVersionService);
    if (uiVersion.version() !== 'v3') return true;
    const sub = shellSubPath(state.url);
    if (sub && !hasSharedEquivalent(sub)) return true;
    return inject(Router).parseUrl(equivalentUrl(state.url, 'v3'));
};

/**
 * V1 (legacy) is hidden, not deleted: flip this to true to bring its routes back.
 * While hidden, old V1 URLs redirect to their V2 equivalents (and on to V3 when preferred).
 */
const LEGACY_V1_ENABLED = false;

const legacyV1Routes: Routes = [
    // === LEGACY V1 DASHBOARD ===
    { path: 'legacy', component: DashboardComponent, canActivate: [authGuard] },

    // === FAMILY SECTION ===
    // Functionality: List of expenses for the Family context.
    // Component: HomeComponent (Reusable Transaction List)
    { path: 'family', component: HomeComponent, canActivate: [authGuard] },

    // Functionality: Form to Add/Edit a FAMILY expense.
    // Component: ExpenseEntryPageComponent
    { path: 'family/add', loadComponent: () => import('./features/home/expense-entry-page.component').then(m => m.ExpenseEntryPageComponent), canActivate: [authGuard] },
    { path: 'family/edit/:expenseId', loadComponent: () => import('./features/home/expense-entry-page.component').then(m => m.ExpenseEntryPageComponent), canActivate: [authGuard] },

    // === TRIPS SECTION ===
    // Functionality: List of all Trips.
    // Component: TripListComponent
    { path: 'trips', component: TripListComponent, canActivate: [authGuard] },

    // Functionality: Form to Add/Edit a TRIP expense.
    // Component: TripExpenseEntryPageComponent (Separate form for Trip logic)
    { path: 'trips/:tripId/add', loadComponent: () => import('./features/trips/trip-expense-entry-page.component').then(m => m.TripExpenseEntryPageComponent), canActivate: [authGuard] },
    { path: 'trips/:tripId/edit/:expenseId', loadComponent: () => import('./features/trips/trip-expense-entry-page.component').then(m => m.TripExpenseEntryPageComponent), canActivate: [authGuard] },

    // Functionality: Transaction List for a SPECIFIC TRIP.
    // Component: HomeComponent (Reusing the same list component as 'family', but context changes based on route param)
    { path: 'trips/:id', component: HomeComponent, canActivate: [authGuard] },

    // === REPORTS & MISC ===
    { path: 'reports/:type', component: ReportsComponent, canActivate: [authGuard] },
    { path: 'masters/categories/:type', loadComponent: () => import('./features/masters/category-master.component').then(m => m.CategoryMasterComponent), canActivate: [authGuard] },
    { path: 'profile', component: ProfileComponent, canActivate: [authGuard] },
    { path: 'settings', loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent), canActivate: [authGuard] },
    { path: 'family/manage', loadComponent: () => import('./features/family/family-manager.component').then(m => m.FamilyManagerComponent), canActivate: [authGuard] },
    { path: 'habits', loadComponent: () => import('./features/habits/habit-dashboard/habit-dashboard.component').then(m => m.HabitDashboardComponent), canActivate: [authGuard] },
    { path: 'feedback', loadComponent: () => import('./features/feedback/feedback.component').then(m => m.FeedbackComponent), canActivate: [authGuard] },
    { path: 'admin', loadComponent: () => import('./features/admin/admin.component').then(m => m.AdminComponent), canActivate: [adminGuard] },
];

const legacyV1Redirects: Routes = [
    { path: 'legacy', redirectTo: '/v2/dashboard' },
    { path: 'family', redirectTo: '/v2/chat', pathMatch: 'full' },
    { path: 'family/add', redirectTo: '/v2/chat' },
    { path: 'family/edit/:expenseId', redirectTo: '/v2/chat' },
    { path: 'family/manage', redirectTo: '/v2/family' },
    { path: 'trips', redirectTo: '/v2/trips', pathMatch: 'full' },
    { path: 'trips/:tripId/add', redirectTo: '/v2/trips/:tripId' },
    { path: 'trips/:tripId/edit/:expenseId', redirectTo: '/v2/trips/:tripId' },
    { path: 'trips/:tripId', redirectTo: '/v2/trips/:tripId' },
    { path: 'reports/:type', redirectTo: '/v2/reports' },
    { path: 'masters/categories/:type', redirectTo: '/v2/categories/:type' },
    { path: 'profile', redirectTo: '/v2/settings' },
    { path: 'settings', redirectTo: '/v2/settings' },
    { path: 'habits', redirectTo: '/v2/habits' },
    { path: 'feedback', redirectTo: '/v2/feedback' },
    { path: 'admin', redirectTo: '/v2/admin' },
];

export const routes: Routes = [
    // === MAIN DASHBOARD (the device's preferred shell, default V2) ===
    { path: '', redirectTo: () => inject(UiVersionService).version(), pathMatch: 'full' },

    // === AUTH ===
    { path: 'login', loadComponent: () => import('./features/v3/v3-auth/login-gate').then(m => m.LoginGate) },

    // === V2 EXPERIMENTAL (Chat UI) ===
    { 
        path: 'v2', 
        loadComponent: () => import('./features/v2/v2-layout/v2-layout').then(m => m.V2Layout),
        canActivate: [authGuard, v2VersionGuard],
        runGuardsAndResolvers: 'always',
        children: [
            { path: 'dashboard', loadComponent: () => import('./features/v2/v2-dashboard/v2-dashboard').then(m => m.V2Dashboard) },
            { path: 'chat', loadComponent: () => import('./features/v2/chat-dashboard/chat-dashboard').then(m => m.ChatDashboard) },
            { path: 'trips', loadComponent: () => import('./features/v2/v2-trips/v2-trips').then(m => m.V2Trips) },
            { path: 'trips/:tripId', loadComponent: () => import('./features/v2/v2-trip-chat/v2-trip-chat').then(m => m.V2TripChat) },
            { path: 'trips/:tripId/report', loadComponent: () => import('./features/v2/v2-trip-report/v2-trip-report').then(m => m.V2TripReport) },
            { path: 'reports', loadComponent: () => import('./features/v2/v2-reports/v2-reports').then(m => m.V2Reports) },
            { path: 'settings', loadComponent: () => import('./features/v2/v2-settings/v2-settings').then(m => m.V2Settings) },
            { path: 'categories/:type', loadComponent: () => import('./features/v2/v2-categories/v2-categories').then(m => m.V2Categories) },
            { path: 'feedback', loadComponent: () => import('./features/v2/v2-feedback/v2-feedback').then(m => m.V2Feedback) },
            { path: 'habits', loadComponent: () => import('./features/v2/v2-habits/v2-habits').then(m => m.V2Habits) },
            { path: 'admin', loadComponent: () => import('./features/v2/v2-admin/v2-admin').then(m => m.V2Admin) },
            { path: 'backup', loadComponent: () => import('./features/v2/v2-backup/v2-backup').then(m => m.V2Backup) },
            { path: 'family', loadComponent: () => import('./features/v2/v2-family/v2-family').then(m => m.V2Family) },
            { path: 'onboarding', loadComponent: () => import('./features/v2/v2-onboarding/v2-onboarding').then(m => m.V2Onboarding) },
            { path: 'notifications', loadComponent: () => import('./features/v2/v2-notifications/v2-notifications').then(m => m.V2Notifications) },
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
        ]
    },

    // === V3 (Kanithan v3 design, built alongside v2) ===
    // Children without a v3 screen yet reuse the v2 screens; each gets replaced one by one.
    {
        path: 'v3',
        loadComponent: () => import('./features/v3/v3-layout/v3-layout').then(m => m.V3Layout),
        canActivate: [authGuard],
        children: [
            { path: 'dashboard', loadComponent: () => import('./features/v3/v3-dashboard/v3-dashboard').then(m => m.V3Dashboard) },
            { path: 'notifications', loadComponent: () => import('./features/v2/v2-notifications/v2-notifications').then(m => m.V2Notifications) },
            { path: 'chat', loadComponent: () => import('./features/v3/v3-chat/v3-chat').then(m => m.V3Chat) },
            { path: 'trips', loadComponent: () => import('./features/v3/v3-trips/v3-trips').then(m => m.V3Trips) },
            { path: 'trips/:tripId', loadComponent: () => import('./features/v3/v3-trip-chat/v3-trip-chat').then(m => m.V3TripChat) },
            { path: 'trips/:tripId/report', loadComponent: () => import('./features/v3/v3-trip-report/v3-trip-report').then(m => m.V3TripReport) },
            { path: 'reports', loadComponent: () => import('./features/v3/v3-reports/v3-reports').then(m => m.V3Reports) },
            { path: 'settings', loadComponent: () => import('./features/v3/v3-settings/v3-settings').then(m => m.V3Settings) },
            { path: 'habits', loadComponent: () => import('./features/v3/v3-habits/v3-habits').then(m => m.V3Habits) },
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' }
        ]
    },

    // === V1 (hidden unless LEGACY_V1_ENABLED) ===
    ...(LEGACY_V1_ENABLED ? legacyV1Routes : legacyV1Redirects),

    { path: '**', redirectTo: '' }
];
